import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/solid';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

export interface MultiSelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface MultiSelectDropdownProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  name?: string;
  id?: string;
  size?: 'default' | 'compact';
  allLabel?: string; // Label to show when all or none selected (e.g., "All")
  /**
   * Show a search box above the list. Worth turning on as soon as the list can
   * outgrow a screenful — scrolling a long list of names to find one person is
   * the thing a checkbox menu is worst at.
   */
  searchable?: boolean;
  /**
   * Show a "Select all / Clear" row. It toggles: everything ticked → Clear,
   * anything else → Select all. Ticking one by one to reach "all" is busywork
   * on any list longer than a handful.
   */
  showSelectAll?: boolean;
}

/**
 * MultiSelectDropdown - Custom multi-select dropdown with checkboxes
 *
 * Features:
 * - Checkbox-based selection for multiple values
 * - Optional search box and select-all row (`searchable`, `showSelectAll`)
 * - Styled dropdown menu using global dropdown CSS classes
 * - Portal-based positioning for correct layering
 * - Keyboard navigation support
 * - Shows selected count or "All" when empty/all selected
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  values,
  onChange,
  options,
  placeholder,
  disabled = false,
  error = false,
  className = '',
  name,
  id,
  size = 'default',
  allLabel,
  searchable = false,
  showSelectAll = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const visibleOptions = query.trim()
    ? options.filter((option) =>
        option.label.toLowerCase().includes(query.trim().toLowerCase())
      )
    : options;

  const allSelected = options.length > 0 && options.every((o) => values.includes(o.value));

  // Use translated placeholder/allLabel if not provided
  const effectiveAllLabel = allLabel || t('common:filters.all', 'All');
  const effectivePlaceholder = placeholder || effectiveAllLabel;

  // Determine display text
  const getDisplayText = () => {
    if (values.length === 0) {
      return effectivePlaceholder;
    }
    if (values.length === 1) {
      const selected = options.find(o => o.value === values[0]);
      return selected?.label || values[0];
    }
    return t('common:filters.selectedCount', '{{count}} selected', { count: values.length });
  };

  const displayText = getDisplayText();
  const hasSelection = values.length > 0;

  // Calculate position when opening - smart positioning to avoid overflow
  const handleToggle = () => {
    if (!isOpen && triggerRef.current && !disabled) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Estimate dropdown height (roughly 44px per option with checkbox, max 280px)
      // plus the fixed header rows, which do not scroll away with the list.
      const headerHeight = (searchable ? 48 : 0) + (showSelectAll ? 36 : 0);
      const estimatedDropdownHeight = Math.min(options.length * 44 + 16 + headerHeight, 280);

      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Position above if not enough space below and more space above
      const shouldPositionAbove = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;

      // Clamp horizontally: the menu is wider than narrow triggers (min 200px),
      // so anchoring at rect.left can push it past the right viewport edge on
      // mobile (e.g. a right-aligned trigger) — shift it back into view.
      const dropdownWidth = Math.max(rect.width, 200); // Min width for checkboxes
      let left = rect.left;
      if (left + dropdownWidth > viewportWidth) {
        left = Math.max(8, viewportWidth - dropdownWidth - 8);
      }

      setDropdownStyle({
        position: 'fixed',
        ...(shouldPositionAbove
          ? { bottom: viewportHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }
        ),
        left,
        width: dropdownWidth,
        maxHeight: shouldPositionAbove ? spaceAbove - 8 : spaceBelow - 8,
        zIndex: 1000
      });
    }
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target) &&
          dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  // A search box the reader has to click into first saves them nothing, and the
  // query from last time must not still be filtering the list on reopen.
  useEffect(() => {
    if (isOpen && searchable) {
      searchRef.current?.focus();
    } else if (!isOpen) {
      setQuery('');
    }
  }, [isOpen, searchable]);

  const handleToggleOption = (optionValue: string) => {
    const newValues = values.includes(optionValue)
      ? values.filter(v => v !== optionValue)
      : [...values, optionValue];
    onChange(newValues);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        handleToggle();
      }
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger Button - styled to match filter inputs */}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`filter-trigger ${error ? 'border-red-300' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={placeholder}
      >
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            lineHeight: 1,
            color: hasSelection ? '#374151' : '#6B7280',
          }}
        >
          {displayText}
        </span>

        <span className="filter-trigger-icon pointer-events-none">
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            } ${
              error ? 'text-red-400' : disabled ? 'text-gray-300' : 'text-gray-400'
            }`}
          />
        </span>
      </button>

      {/* Dropdown Menu with Portal */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 flex flex-col overflow-hidden"
          style={dropdownStyle}
          role="listbox"
          aria-label={placeholder}
          aria-multiselectable="true"
        >
          {/* Search and select-all stay put while the list scrolls under them —
              inside the scrolling area they would disappear on the first swipe,
              which is exactly when a long list needs them. */}
          {(searchable || showSelectAll) && (
            <div className="flex-shrink-0 border-b border-gray-100 p-2 space-y-1.5">
              {searchable && (
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('common:filters.search', 'Search…')}
                    className="w-full rounded-md border border-gray-300 pl-7 pr-2 py-1.5 text-xs focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              )}
              {showSelectAll && (
                <button
                  type="button"
                  onClick={() =>
                    onChange(allSelected ? [] : options.map((option) => option.value))
                  }
                  className="w-full text-left text-xs text-primary-700 hover:text-primary-800 px-1 py-0.5"
                >
                  {allSelected
                    ? t('common:filters.clearAll', 'Clear all')
                    : t('common:filters.selectAll', 'Select all')}
                </button>
              )}
            </div>
          )}

          <div className="overflow-y-auto py-1">
          {visibleOptions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500 text-center">
              {options.length === 0
                ? t('common:filters.noOptions', 'No options available')
                : t('common:filters.noMatches', 'Nothing matches that search')}
            </div>
          ) : (
            visibleOptions.map((option) => {
              const isSelected = values.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => !option.disabled && handleToggleOption(option.value)}
                  disabled={option.disabled}
                  className={`flex items-center w-full px-3 py-2 text-left cursor-pointer transition-colors ${
                    isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                  } ${
                    option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  role="option"
                  aria-selected={isSelected}
                >
                  {/* Checkbox */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-primary-600 border-primary-600'
                        : 'border-gray-300 bg-white'
                    }`}>
                      {isSelected && (
                        <CheckIcon className="w-2.5 h-2.5 text-white" />
                      )}
                    </div>
                    <div className={option.description ? 'flex flex-col text-left min-w-0' : 'min-w-0'}>
                      <span className="text-xs text-gray-900 truncate">
                        {option.label}
                      </span>
                      {option.description && (
                        <span className="text-[10px] text-gray-500 mt-0.5 font-normal truncate">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MultiSelectDropdown;
